import type { TrailerTypeInput, TruckClassInput, TyreLayout, TyreLayoutInput, TyreLayoutSubjectKind } from '@chukua/common';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { ApiError } from '../../../../../shared/shared.api';
import {
  createTrailerType,
  createTruckClass,
  listTrailerTypes,
  listTruckClasses,
  updateTrailerType,
  updateTruckClass,
  type TrailerType,
  type TruckClass,
} from '../../../reference-data.api';
import { getTyreLayout, saveTyreLayout } from '../tyre-layouts.api';
import { vehicleClassPath } from '../tyre-layouts.paths';

export type VehicleClass = TruckClass | TrailerType;
export type VehicleClassInput = TruckClassInput | TrailerTypeInput;

export type VehicleClassState =
  { status: 'loading' } | { status: 'loaded'; subject: VehicleClass | null; layout: TyreLayout | null } | { status: 'missing' } | { status: 'error' };

const NOUN: Record<TyreLayoutSubjectKind, string> = { truck_class: 'truck class', trailer_type: 'trailer type' };

async function loadVehicleClass(kind: TyreLayoutSubjectKind, code: string): Promise<VehicleClassState> {
  const rows: VehicleClass[] = kind === 'truck_class' ? await listTruckClasses() : await listTrailerTypes();
  const subject = rows.find((row) => row.code === code);
  if (!subject) return { status: 'missing' };
  return { status: 'loaded', subject, layout: await getTyreLayout(kind, code) };
}

function persistDetails(kind: TyreLayoutSubjectKind, subject: VehicleClass | null, input: VehicleClassInput): Promise<VehicleClass> {
  if (kind === 'truck_class') {
    return subject ? updateTruckClass(subject.id, input as TruckClassInput) : createTruckClass(input as TruckClassInput);
  }
  return subject ? updateTrailerType(subject.id, input as TrailerTypeInput) : createTrailerType(input as TrailerTypeInput);
}

function describeSaveError(kind: TyreLayoutSubjectKind, err: unknown): string {
  if (err instanceof ApiError && err.status === 409) return `A ${NOUN[kind]} with this code already exists.`;
  return 'Could not save — check the fields and try again.';
}

/**
 * One truck class or trailer type with its tyre layout (gitaumoses4/chukua#176
 * follow-up): loaded by code, or empty for a new one (`code` null). A save
 * writes the row's details, then the layout when the arrangement changed, and
 * a new row's page moves to its own address once it exists.
 */
export function useVehicleClass(kind: TyreLayoutSubjectKind, code: string | null) {
  const [state, setState] = useState<VehicleClassState>(code ? { status: 'loading' } : { status: 'loaded', subject: null, layout: null });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    loadVehicleClass(kind, code)
      .then((loaded) => {
        if (!cancelled) setState(loaded);
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [kind, code]);

  /**
   * The details first: once a new row exists the page is its page, so a
   * layout save that fails afterwards is retried as an edit, never a second
   * create.
   */
  async function save(details: VehicleClassInput, layout: TyreLayoutInput | null): Promise<boolean> {
    const before = state.status === 'loaded' ? state : { subject: null, layout: null };
    setSaveError(null);
    setSaving(true);
    let saved: VehicleClass | null = null;
    try {
      saved = await persistDetails(kind, before.subject, details);
      const savedLayout = layout ? await saveTyreLayout(kind, saved.code, layout) : before.layout;
      setState({ status: 'loaded', subject: saved, layout: savedLayout });
      toast.success(`${saved.name} ${before.subject ? 'updated' : 'created'}`);
      return true;
    } catch (err) {
      if (saved) setState({ status: 'loaded', subject: saved, layout: before.layout });
      setSaveError(describeSaveError(kind, err));
      return false;
    } finally {
      setSaving(false);
      if (saved && !before.subject) navigate(vehicleClassPath(kind, saved.code), { replace: true });
    }
  }

  return { state, saveError, saving, save };
}
